import React, { useCallback, useMemo, useState } from "react";
import { Select, Button, Divider } from "antd";
import { PlusOutlined } from "@ant-design/icons";

const defaultFilterOption = (input, option) =>
  String(option?.label ?? "")
    .toLowerCase()
    .includes(input.toLowerCase());

const SearchableSelectWithAdd = ({
  options = [],
  value,
  onChange,
  valueKey = "id",
  labelKey = "name",
  FormComponent,
  onFormCreate,
  nameFieldForPrefill = "name",
  addNewLabel,
  allowAddNew = true,
  placeholder = "Select",
  disabled = false,
  formProps = {},
  filterOption = defaultFilterOption,
  ...selectProps
}) => {
  const [searchText, setSearchText] = useState("");
  const [formVisible, setFormVisible] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(null);

  const selectOptions = useMemo(
    () =>
      options.map((item) => ({
        value: item[valueKey],
        label: String(item[labelKey] ?? ""),
      })),
    [options, valueKey, labelKey]
  );

  const trimmedSearch = searchText.trim();

  const hasMatch = useMemo(() => {
    if (!trimmedSearch) return true;
    const q = trimmedSearch.toLowerCase();
    return selectOptions.some((opt) => opt.label.toLowerCase() === q);
  }, [trimmedSearch, selectOptions]);

  const showAddNew =
    allowAddNew &&
    Boolean(FormComponent) &&
    typeof onFormCreate === "function" &&
    Boolean(trimmedSearch) &&
    !hasMatch;

  const resolveAddLabel = () => {
    if (typeof addNewLabel === "function") return addNewLabel(trimmedSearch);
    if (typeof addNewLabel === "string") return addNewLabel;
    return `Add "${trimmedSearch}"`;
  };

  const openAddForm = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setFormInitialValues({ [nameFieldForPrefill]: trimmedSearch });
      setFormVisible(true);
    },
    [nameFieldForPrefill, trimmedSearch]
  );

  const closeAddForm = useCallback(() => {
    setFormVisible(false);
    setFormInitialValues(null);
  }, []);

  const handleFormCreate = useCallback(
    async (values) => {
      const result = await onFormCreate(values);
      closeAddForm();
      setSearchText("");

      if (result === undefined || result === null) return;

      const newValue =
        typeof result === "object" && result !== null
          ? result[valueKey]
          : result;

      if (newValue !== undefined && newValue !== null) {
        onChange?.(newValue);
      }
    },
    [closeAddForm, onChange, onFormCreate, valueKey]
  );

  const dropdownRender = useCallback(
    (menu) => (
      <>
        {menu}
        {showAddNew && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div style={{ padding: "4px 8px 8px" }}>
              <Button
                type="link"
                icon={<PlusOutlined />}
                block
                onMouseDown={(e) => e.preventDefault()}
                onClick={openAddForm}
                style={{ textAlign: "left", padding: 0 }}
              >
                {resolveAddLabel()}
              </Button>
            </div>
          </>
        )}
      </>
    ),
    [openAddForm, showAddNew, addNewLabel, trimmedSearch]
  );

  const handleDropdownVisibleChange = (open) => {
    if (!open) setSearchText("");
    selectProps.onDropdownVisibleChange?.(open);
  };

  return (
    <>
      <Select
        {...selectProps}
        placeholder={placeholder}
        disabled={disabled}
        allowClear
        showSearch
        optionFilterProp="label"
        options={selectOptions}
        value={value}
        onChange={onChange}
        searchValue={searchText}
        onSearch={setSearchText}
        filterOption={filterOption}
        dropdownRender={dropdownRender}
        onDropdownVisibleChange={handleDropdownVisibleChange}
      />

      {FormComponent && (
        <FormComponent
          visible={formVisible}
          onCancel={closeAddForm}
          onCreate={handleFormCreate}
          record={null}
          initialValues={formInitialValues}
          {...formProps}
        />
      )}
    </>
  );
};

export default SearchableSelectWithAdd;
